import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { X, Upload, FileText, XCircle } from 'lucide-react';
import { Document, Task } from '../../../types';

interface DocumentUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (document: Omit<Document, 'id' | 'uploadDate'>) => void;
  tasks?: Task[];
  onTaskComplete?: (taskId: string) => void;
  isMobile?: boolean;
}

export const DocumentUploadDialog: React.FC<DocumentUploadDialogProps> = ({
  open,
  onClose,
  onUpload,
  tasks,
  onTaskComplete,
  isMobile,
}) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    url: '',
    uploadedBy: '1', // Current user ID
  });

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [completeTask, setCompleteTask] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    
    // If no title is set and only one file is being added, use the file name as title
    if (!formData.title && acceptedFiles.length === 1) {
      const fileName = acceptedFiles[0].name.replace(/\.[^/.]+$/, ""); // Remove extension
      setFormData(prev => ({ ...prev, title: fileName }));
    }
  }, [formData.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    }
  });

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would upload the file here
    // For now, we'll just simulate it by creating a fake URL
    onUpload({
      ...formData,
      url: selectedFiles.length > 0 ? URL.createObjectURL(selectedFiles[0]) : formData.url,
    });

    // If a task was selected and completeTask is checked, complete the task
    if (completeTask && selectedTaskId && onTaskComplete) {
      onTaskComplete(selectedTaskId);
    }

    // Reset form
    setFormData({
      title: '',
      type: '',
      url: '',
      uploadedBy: '1',
    });
    setSelectedFiles([]);
    setSelectedTaskId('');
    setCompleteTask(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const incompleteTasks = tasks?.filter(task => task.status !== 'completed') || [];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          m: isMobile ? 2 : 3,
          borderRadius: 2,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Typography variant="h6" component="div">
            Upload Document
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: 'text.secondary' }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                bgcolor: 'background.default',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Upload 
                  size={32}
                  className={isDragActive ? 'text-primary' : 'text-gray-400'}
                />
                <Typography align="center" color="text.secondary">
                  {isDragActive ? (
                    'Drop the file here'
                  ) : (
                    <>
                      Drag and drop your file here, or click to select
                      <br />
                      <Typography variant="caption" component="span" sx={{ mt: 0.5, display: 'block' }}>
                        Supported formats: PDF, DOC, DOCX, JPG, PNG
                      </Typography>
                    </>
                  )}
                </Typography>
              </Box>
            </Paper>

            {selectedFiles.length > 0 && (
              <List>
                {selectedFiles.map((file, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      bgcolor: 'background.default',
                      borderRadius: 1,
                      mb: 1,
                    }}
                  >
                    <ListItemIcon>
                      <FileText size={20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: { fontWeight: 500 }
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption'
                      }}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => removeFile(index)}
                      >
                        <XCircle size={18} />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              fullWidth
              label="Document Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={formData.type}
                label="Document Type"
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="legal">Legal Document</MenuItem>
                <MenuItem value="medical">Medical Record</MenuItem>
                <MenuItem value="financial">Financial Document</MenuItem>
                <MenuItem value="evidence">Evidence</MenuItem>
                <MenuItem value="correspondence">Correspondence</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            {selectedFiles.length === 0 && (
              <TextField
                fullWidth
                label="Document URL"
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                required={selectedFiles.length === 0}
                helperText="Enter the URL where the document is stored"
              />
            )}

            {incompleteTasks.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Complete Task</InputLabel>
                  <Select
                    value={selectedTaskId}
                    label="Complete Task"
                    onChange={(e) => {
                      setSelectedTaskId(e.target.value);
                      setCompleteTask(Boolean(e.target.value));
                    }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {incompleteTasks.map((task) => (
                      <MenuItem key={task.id} value={task.id}>
                        {task.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedTaskId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={completeTask}
                        onChange={(e) => setCompleteTask(e.target.checked)}
                      />
                    }
                    label="Mark task as completed"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ 
          px: 3, 
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
        }}>
          <Button 
            onClick={onClose} 
            color="inherit"
            fullWidth={isMobile}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<Upload size={18} />}
            fullWidth={isMobile}
          >
            Upload
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};